import { create } from "zustand";

import { logger } from "@/telemetry";

/**
 * UI Preferences Store
 *
 * Manages user interface preferences with localStorage persistence.
 * Provides a single source of truth for UI toggles across the application.
 */

/**
 * Export font options for Word document exports.
 * "brand" uses the current brand's font family (e.g. Poppins for WebuildAI, Helvetica Neue for National Grid)
 */
export type ExportFontOption = "brand" | "Poppins" | "Calibri" | "Arial" | "Times New Roman";

/**
 * Per-control pinned state for the chat input toolbar.
 * Each key maps to whether that control is always visible (pinned) in the toolbar.
 */
export interface PinnedChatControls {
  prompts: boolean;
  prompto: boolean;
  search: boolean;
  knowledgeHub: boolean;
  configuration: boolean;
  modelSelector: boolean;
}

/** Union of all valid pinned-control keys. */
export type PinnedChatControlKey = keyof PinnedChatControls;

/**
 * Collapsed state of the Workflow Builder's three panes (list, build-agent
 * chat, canvas). The chat and canvas panes are mutually exclusive collapse
 * targets — collapsing one hands its width to the other — so both being
 * collapsed at once would leave the shell with nothing to show.
 */
export interface WorkflowBuilderCollapsedPanes {
  list: boolean;
  chat: boolean;
  canvas: boolean;
}

/** Union of all valid Workflow Builder pane keys. */
export type WorkflowBuilderPane = keyof WorkflowBuilderCollapsedPanes;

/**
 * localStorage key for per-workflow run notification preferences (LSEG-107).
 *
 * This is a device-local display preference, not a subscription. `Notification`
 * in agent-platform has no `workflowId`, no subscription entity exists anywhere,
 * and notifications were explicitly ruled out of scope on the backend side, so
 * nothing is delivered to anyone. It lives here rather than on the workflow record
 * precisely because it is not something the backend honours, and the confirmation
 * dialog says so before it is flipped in either direction.
 */
const AUTOMATION_NOTIFY_KEY = "pathway.automations.notify";

/** localStorage key the Workflow Builder's collapsed-pane state is persisted under. */
const WORKFLOW_BUILDER_COLLAPSED_PANES_KEY = "pathway.workflowBuilder.collapsedPanes";

/** Default value for WorkflowBuilderCollapsedPanes. All panes start expanded. */
const DEFAULT_WORKFLOW_BUILDER_COLLAPSED_PANES: WorkflowBuilderCollapsedPanes = {
  list: false,
  chat: false,
  canvas: false,
};

/**
 * Clamps an illegal Workflow Builder collapsed-pane state where both the
 * chat and canvas panes are collapsed simultaneously. The shell needs at
 * least one of the two visible, so chat is forced back open in that case.
 * Applied both when hydrating from localStorage and whenever a pane's
 * collapsed state is set, so the invariant always holds.
 */
function clampWorkflowBuilderCollapsedPanes(
  panes: WorkflowBuilderCollapsedPanes,
): WorkflowBuilderCollapsedPanes {
  if (panes.chat && panes.canvas) {
    return { ...panes, chat: false };
  }

  return panes;
}

/**
 * Default value for PinnedChatControls.
 * All controls default to false (hidden until the user explicitly enables them).
 * The model selector can be toggled on via the "/" command menu or Settings.
 */
const DEFAULT_PINNED_CHAT_CONTROLS: PinnedChatControls = {
  prompts: false,
  prompto: false,
  search: false,
  knowledgeHub: false,
  configuration: false,
  modelSelector: false,
};

/**
 * Font option configuration with system-safe metadata.
 * isSystemSafe indicates whether the font is pre-installed on most computers.
 *
 * IMPORTANT: Bundled fonts (Poppins, Helvetica Neue) are NOT system-safe for exports.
 * They only work in the web app via @font-face. DOCX files don't embed fonts,
 * so recipients without these fonts installed will see a substituted font.
 */
export const EXPORT_FONT_OPTIONS: {
  value: ExportFontOption;
  label: string;
  isSystemSafe: boolean;
}[] = [
  { value: "brand", label: "Use Brand Font", isSystemSafe: false }, // Bundled brand font - NOT pre-installed
  { value: "Poppins", label: "Poppins", isSystemSafe: false }, // NOT pre-installed on most systems
  { value: "Calibri", label: "Calibri", isSystemSafe: true }, // Pre-installed on Windows, macOS
  { value: "Arial", label: "Arial", isSystemSafe: true }, // Pre-installed on all systems
  { value: "Times New Roman", label: "Times New Roman", isSystemSafe: true }, // Pre-installed on all systems
];

/**
 * Page size options for Word document exports.
 * A4 is the default (standard international size).
 * Labels and dimensions use i18n translation keys (pageSize.*).
 */
export type ExportPageSize = "A4" | "Letter" | "Legal";

export const EXPORT_PAGE_SIZE_OPTIONS: {
  value: ExportPageSize;
  labelKey: string;
  dimensionsKey: string;
}[] = [
  { value: "A4", labelKey: "pageSize.A4", dimensionsKey: "pageSize.A4.dimensions" },
  { value: "Letter", labelKey: "pageSize.Letter", dimensionsKey: "pageSize.Letter.dimensions" },
  { value: "Legal", labelKey: "pageSize.Legal", dimensionsKey: "pageSize.Legal.dimensions" },
];

export interface UIPreferencesState {
  // Line numbers display preference for code artefacts
  showLineNumbers: boolean;

  // Export font preference for Word documents
  exportFontFamily: ExportFontOption;

  // Export page size preference for Word documents
  exportPageSize: ExportPageSize;

  // Show advanced download options (PNG, CSV, Markdown, HTML, Text)
  showAdvancedDownloadOptions: boolean;

  // Whether the chat input is in simple (single-line) mode
  simpleChatInput: boolean;

  // Which chat input toolbar controls are pinned (always visible)
  pinnedChatControls: PinnedChatControls;

  // Whether web search is enabled by default for new chat sessions
  webSearchDefault: boolean;

  // Whether knowledge hub is enabled by default for new chat sessions
  knowledgeHubDefault: boolean;

  // Whether the simple chat input mode hint banner has been dismissed
  simpleChatInputHintDismissed: boolean;

  // Whether active-feature chips (Web Search, Knowledge Hub) are shown
  // next to the model selector in the action row (default false)
  showActiveFeatureChips: boolean;

  // Which Workflow Builder panes (list, build-agent chat, canvas) are collapsed
  workflowBuilderCollapsedPanes: WorkflowBuilderCollapsedPanes;

  // Workflow id to whether run notifications are wanted for it. Device-local and
  // not delivered anywhere; see AUTOMATION_NOTIFY_KEY.
  automationNotify: Record<string, boolean>;

  // Actions
  toggleLineNumbers: () => void;
  setExportFontFamily: (font: ExportFontOption) => void;
  setExportPageSize: (size: ExportPageSize) => void;
  toggleAdvancedDownloadOptions: () => void;
  toggleSimpleChatInput: () => void;
  setSimpleChatInput: (value: boolean) => void;
  setPinnedChatControl: (key: PinnedChatControlKey, value: boolean) => void;
  setWebSearchDefault: (value: boolean) => void;
  setKnowledgeHubDefault: (value: boolean) => void;
  dismissSimpleChatInputHint: () => void;
  setShowActiveFeatureChips: (value: boolean) => void;
  setWorkflowBuilderPaneCollapsed: (pane: WorkflowBuilderPane, collapsed: boolean) => void;
  setAutomationNotify: (workflowId: string, notify: boolean) => void;
}

export const useUIPreferences = create<UIPreferencesState>((set, get) => ({
  // Initialize from localStorage
  showLineNumbers: (() => {
    try {
      const stored = localStorage.getItem("artefact-show-line-numbers");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Initialize export font from localStorage (default to "brand")
  exportFontFamily: (() => {
    try {
      const stored = localStorage.getItem("export-font-family");

      if (stored) {
        const parsed = JSON.parse(stored);

        // Validate that the stored value is a valid option
        const validOptions: ExportFontOption[] = [
          "brand",
          "Poppins",
          "Calibri",
          "Arial",
          "Times New Roman",
        ];

        if (validOptions.includes(parsed)) {
          return parsed as ExportFontOption;
        }
      }

      return "brand";
    } catch {
      return "brand";
    }
  })(),

  // Initialize export page size from localStorage (default to "A4")
  exportPageSize: (() => {
    try {
      const stored = localStorage.getItem("export-page-size");

      if (stored) {
        const parsed = JSON.parse(stored);

        // Validate that the stored value is a valid option
        const validOptions: ExportPageSize[] = ["A4", "Letter", "Legal"];

        if (validOptions.includes(parsed)) {
          return parsed as ExportPageSize;
        }
      }

      return "A4";
    } catch {
      return "A4";
    }
  })(),

  // Initialize advanced download options from localStorage (default to false)
  showAdvancedDownloadOptions: (() => {
    try {
      const stored = localStorage.getItem("show-advanced-download-options");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Initialise simple chat input mode from localStorage (default to true)
  simpleChatInput: (() => {
    try {
      const stored = localStorage.getItem("chat-input-simple-mode");

      return stored ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  })(),

  // Initialise pinned chat controls from localStorage (default all false).
  // Validates that the stored value is an object with exactly the expected keys;
  // falls back to all-false on any shape mismatch or parse error.
  pinnedChatControls: (() => {
    try {
      const stored = localStorage.getItem("chat-input-pinned-controls");

      if (stored) {
        const parsed: unknown = JSON.parse(stored);

        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          const expectedKeys: PinnedChatControlKey[] = [
            "prompts",
            "prompto",
            "search",
            "knowledgeHub",
            "configuration",
            "modelSelector",
          ];
          const obj = parsed as Record<string, unknown>;
          const allKeysPresent = expectedKeys.every((k) => typeof obj[k] === "boolean");

          if (allKeysPresent) {
            return parsed as PinnedChatControls;
          }
        }
      }

      return { ...DEFAULT_PINNED_CHAT_CONTROLS };
    } catch {
      return { ...DEFAULT_PINNED_CHAT_CONTROLS };
    }
  })(),

  // Initialise web search default from localStorage.
  // Migration: if "web-search-default" is absent, reads the legacy "webSearchEnabled" key.
  webSearchDefault: (() => {
    try {
      const stored = localStorage.getItem("web-search-default");

      if (stored !== null) {
        return JSON.parse(stored) as boolean;
      }

      // Legacy migration: fall back to "webSearchEnabled" if present
      const legacy = localStorage.getItem("webSearchEnabled");

      if (legacy !== null) {
        return JSON.parse(legacy) as boolean;
      }

      return false;
    } catch {
      return false;
    }
  })(),

  // Initialise knowledge hub default from localStorage (default to false)
  knowledgeHubDefault: (() => {
    try {
      const stored = localStorage.getItem("knowledge-hub-default");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Initialise simple chat input hint dismissed state from localStorage (default to false)
  simpleChatInputHintDismissed: (() => {
    try {
      const stored = localStorage.getItem("chat-input-hint-dismissed");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Initialise active-feature chips visibility from localStorage (default to false)
  showActiveFeatureChips: (() => {
    try {
      const stored = localStorage.getItem("chat-input-show-active-chips");

      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  })(),

  // Initialise Workflow Builder collapsed panes from localStorage (default all expanded).
  // Validates that the stored value is an object with exactly the expected keys;
  // falls back to all-expanded on any shape mismatch or parse error. The illegal
  // chat+canvas-both-collapsed state is clamped on every hydration.
  workflowBuilderCollapsedPanes: (() => {
    try {
      const stored = localStorage.getItem(WORKFLOW_BUILDER_COLLAPSED_PANES_KEY);

      if (stored) {
        const parsed: unknown = JSON.parse(stored);

        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          const expectedKeys: WorkflowBuilderPane[] = ["list", "chat", "canvas"];
          const obj = parsed as Record<string, unknown>;
          const allKeysPresent = expectedKeys.every((k) => typeof obj[k] === "boolean");

          if (allKeysPresent) {
            return clampWorkflowBuilderCollapsedPanes(parsed as WorkflowBuilderCollapsedPanes);
          }
        }
      }

      return { ...DEFAULT_WORKFLOW_BUILDER_COLLAPSED_PANES };
    } catch {
      return { ...DEFAULT_WORKFLOW_BUILDER_COLLAPSED_PANES };
    }
  })(),

  // Initialise per-workflow notification preferences from localStorage (default none).
  // Only boolean entries are kept, so a corrupt or hand-edited value cannot put a
  // non-boolean into the map and reach a switch as an object.
  automationNotify: (() => {
    try {
      const stored = localStorage.getItem(AUTOMATION_NOTIFY_KEY);

      if (!stored) return {};

      const parsed: unknown = JSON.parse(stored);

      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};

      const cleaned: Record<string, boolean> = {};

      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof value === "boolean") cleaned[key] = value;
      }

      return cleaned;
    } catch {
      return {};
    }
  })(),

  // Toggle line numbers and persist to localStorage
  toggleLineNumbers: () => {
    const newValue = !get().showLineNumbers;

    set({ showLineNumbers: newValue });

    try {
      localStorage.setItem("artefact-show-line-numbers", JSON.stringify(newValue));
    } catch (error) {
      logger.warn("Failed to save line numbers preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set export font family and persist to localStorage
  setExportFontFamily: (font: ExportFontOption) => {
    set({ exportFontFamily: font });

    try {
      localStorage.setItem("export-font-family", JSON.stringify(font));
    } catch (error) {
      logger.warn("Failed to save export font preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set export page size and persist to localStorage
  setExportPageSize: (size: ExportPageSize) => {
    set({ exportPageSize: size });

    try {
      localStorage.setItem("export-page-size", JSON.stringify(size));
    } catch (error) {
      logger.warn("Failed to save export page size preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Toggle advanced download options and persist to localStorage
  toggleAdvancedDownloadOptions: () => {
    const newValue = !get().showAdvancedDownloadOptions;

    set({ showAdvancedDownloadOptions: newValue });

    try {
      localStorage.setItem("show-advanced-download-options", JSON.stringify(newValue));
    } catch (error) {
      logger.warn("Failed to save advanced download options preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Toggle simple chat input mode and persist to localStorage
  toggleSimpleChatInput: () => {
    const newValue = !get().simpleChatInput;

    set({ simpleChatInput: newValue });

    try {
      localStorage.setItem("chat-input-simple-mode", JSON.stringify(newValue));
    } catch (error) {
      logger.warn("Failed to save simple chat input preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set simple chat input mode explicitly and persist to localStorage
  setSimpleChatInput: (value: boolean) => {
    set({ simpleChatInput: value });

    try {
      localStorage.setItem("chat-input-simple-mode", JSON.stringify(value));
    } catch (error) {
      logger.warn("Failed to save simple chat input preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Update a single pinned chat control and persist the whole object to localStorage
  setPinnedChatControl: (key: PinnedChatControlKey, value: boolean) => {
    const updated: PinnedChatControls = { ...get().pinnedChatControls, [key]: value };

    set({ pinnedChatControls: updated });

    try {
      localStorage.setItem("chat-input-pinned-controls", JSON.stringify(updated));
    } catch (error) {
      logger.warn("Failed to save pinned chat controls preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set web search default and persist to localStorage
  setWebSearchDefault: (value: boolean) => {
    set({ webSearchDefault: value });

    try {
      localStorage.setItem("web-search-default", JSON.stringify(value));
    } catch (error) {
      logger.warn("Failed to save web search default preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set knowledge hub default and persist to localStorage
  setKnowledgeHubDefault: (value: boolean) => {
    set({ knowledgeHubDefault: value });

    try {
      localStorage.setItem("knowledge-hub-default", JSON.stringify(value));
    } catch (error) {
      logger.warn("Failed to save knowledge hub default preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Dismiss the simple chat input hint banner and persist to localStorage
  dismissSimpleChatInputHint: () => {
    set({ simpleChatInputHintDismissed: true });

    try {
      localStorage.setItem("chat-input-hint-dismissed", JSON.stringify(true));
    } catch (error) {
      logger.warn("Failed to save simple chat input hint dismissed state to localStorage", {
        error: String(error),
      });
    }
  },

  // Set active-feature chips visibility and persist to localStorage
  setShowActiveFeatureChips: (value: boolean) => {
    set({ showActiveFeatureChips: value });

    try {
      localStorage.setItem("chat-input-show-active-chips", JSON.stringify(value));
    } catch (error) {
      logger.warn("Failed to save active feature chips preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Update a single Workflow Builder pane's collapsed state, clamp the illegal
  // chat+canvas-both-collapsed combination, and persist the whole object
  setWorkflowBuilderPaneCollapsed: (pane: WorkflowBuilderPane, collapsed: boolean) => {
    const updated = clampWorkflowBuilderCollapsedPanes({
      ...get().workflowBuilderCollapsedPanes,
      [pane]: collapsed,
    });

    set({ workflowBuilderCollapsedPanes: updated });

    try {
      localStorage.setItem(WORKFLOW_BUILDER_COLLAPSED_PANES_KEY, JSON.stringify(updated));
    } catch (error) {
      logger.warn("Failed to save workflow builder collapsed panes preference to localStorage", {
        error: String(error),
      });
    }
  },

  // Set one workflow's run notification preference and persist the whole map
  setAutomationNotify: (workflowId: string, notify: boolean) => {
    const updated = { ...get().automationNotify, [workflowId]: notify };

    set({ automationNotify: updated });

    try {
      localStorage.setItem(AUTOMATION_NOTIFY_KEY, JSON.stringify(updated));
    } catch (error) {
      logger.warn("Failed to save automation notify preference to localStorage", {
        error: String(error),
      });
    }
  },
}));
